import { useEffect, useState } from 'react';

import { listOrganizations, listVenueDirectory, type DirectoryVenue, type Organization } from '../lib/tenancy';
import { useAdminScope } from './scope';

/**
 * The organization / venue context selectors.
 *
 * ── WHAT THESE ARE, AND WHAT THEY ARE NOT ────────────────────────────────────────────
 *
 * They write `?org=` and `?venue=` into the URL. That is the whole of their authority.
 * They do not grant access to anything: every endpoint that reads scope authorizes it
 * server-side against the session, so a hand-typed organization id is a request the
 * engine is free to refuse — see §16 of the task and the note in scope.ts.
 *
 * ── WHY THE VENUE LIST DEPENDS ON THE ORGANIZATION ───────────────────────────────────
 *
 * A venue belongs to exactly one organization. Offering organization A with venue B in
 * one control pair would let someone assemble a combination that cannot exist, and then
 * wonder why the page below is empty. So the venue list is fetched for the selected
 * organization, and a `?venue=` in the URL that does not belong to the selected
 * organization is REMOVED from the URL rather than displayed as if it were in effect.
 *
 * With no organization selected — All Organizations, which a platform admin may use — the
 * venue control is disabled rather than showing a cross-organization list. Cross-org venue
 * selection is not supported, and a control that appears to offer it would be lying.
 *
 * ── PLAIN <select> ON PURPOSE ────────────────────────────────────────────────────────
 *
 * Keyboard support, type-ahead, screen-reader semantics and the mobile picker all come
 * free and correct. A custom dropdown would be a worse version of all four.
 */
export function ContextBar() {
  const { organizationId, venueId, setOrganization, setVenue } = useAdminScope();

  const [orgs, setOrgs] = useState<Organization[] | null>(null);
  const [venues, setVenues] = useState<DirectoryVenue[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listOrganizations()
      .then((rows) => { if (!cancelled) setOrgs(rows); })
      // A failed context fetch must not take the console down with it. Every page below
      // still works; the selector simply says it could not load.
      .catch(() => { if (!cancelled) { setOrgs([]); setUnavailable(true); } });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!organizationId) { setVenues([]); return undefined; }
    listVenueDirectory({ organizationId })
      .then((rows) => { if (!cancelled) setVenues(rows); })
      .catch(() => { if (!cancelled) setVenues([]); });
    return () => { cancelled = true; };
  }, [organizationId]);

  // Normalize an impossible URL rather than render an impossible state.
  useEffect(() => {
    if (!venueId) return;
    if (!organizationId) { setVenue(null); return; }
    if (venues === null) return;
    if (!venues.some((v) => v.venueId === venueId)) setVenue(null);
  }, [venueId, organizationId, venues, setVenue]);

  return (
    <div className="ash-context">
      <label className="ash-context__field">
        <span className="ash-context__label">Organization</span>
        <select
          className="ash-context__select"
          value={organizationId ?? ''}
          disabled={orgs === null}
          onChange={(e) => setOrganization(e.target.value || null)}
        >
          <option value="">All organizations</option>
          {(orgs ?? []).map((org) => (
            <option key={org.id} value={org.id}>{org.name}</option>
          ))}
        </select>
      </label>

      <label className="ash-context__field">
        <span className="ash-context__label">Venue</span>
        <select
          className="ash-context__select"
          value={venueId ?? ''}
          disabled={!organizationId || venues === null}
          title={organizationId ? undefined : 'Select an organization to choose one of its venues.'}
          onChange={(e) => setVenue(e.target.value || null)}
        >
          <option value="">All venues</option>
          {(venues ?? []).map((v) => (
            <option key={`${v.venueKind}:${v.venueId}`} value={v.venueId}>{v.name}</option>
          ))}
        </select>
      </label>

      {unavailable && (
        <p className="ash-context__note" role="status">Context unavailable</p>
      )}
    </div>
  );
}
